import React, { useEffect, useRef } from "react";
import { Stack, SvgIcon, Typography } from "@mui/material";
import { motion, useInView, useReducedMotion } from "framer-motion";

// Each icon's shapes (copied from assets/Icon*.svg so they can move
// independently), with where each starts before assembling: an offset in the
// icon's own viewBox units (CSS px on an SVG element are user units, so it
// scales with the icon) and a starting rotation.
const ICONS = {
  track: {
    viewBox: "0 0 693.07 660.89",
    pieces: [
      {
        d: "M334.61,660.89C149.81,660.89,0,512.94,0,330.45S149.81,0,334.61,0",
        from: { x: -260, y: 0, rotate: -40 },
      },
      {
        d: "M693.07,660.89c-184.8,0-334.61-147.95-334.61-330.45S508.27,0,693.07,0",
        from: { x: 260, y: 0, rotate: 40 },
      },
    ],
  },
  curate: {
    viewBox: "0 0 687.25 660.89",
    pieces: [
      {
        d: "M336.79,660.48C150.78,660.48,0,512.63,0,330.24S150.78,0,336.79,0",
        from: { x: -260, y: 0, rotate: -40 },
      },
      {
        d: "M687.25,312.72c-171.72,0-310.92-139.2-310.92-310.92h310.92v310.92Z",
        from: { x: 220, y: -220, rotate: 60 },
      },
      {
        d: "M378.62,352.26h308.63v308.63h-308.63Z",
        from: { x: 220, y: 220, rotate: -60 },
      },
    ],
  },
  explore: {
    viewBox: "0 0 687.25 660.89",
    pieces: [
      {
        d: "M370.6.1c174.88,0,316.65,142.51,316.65,318.3h-316.65V.1Z",
        from: { x: 220, y: -220, rotate: 60 },
      },
      {
        d: "M0,190.24c258.59,0,468.22,210.72,468.22,470.65H0V190.24Z",
        from: { x: -220, y: 220, rotate: -40 },
      },
    ],
  },
};

const REST = { x: 0, y: 0, rotate: 0, opacity: 1 };
// Seconds between each piece setting off, and before the title follows
const PIECE_STAGGER = 0.12;
const TITLE_DELAY = 0.2;
// Shrinks the viewport's bottom edge for the in-view check, so the heading
// assembles once it's scrolled about a third of the way up the screen —
// where the eye is — rather than the moment it peeks in at the bottom
const TRIGGER_MARGIN = "0px 0px -30% 0px";

/**
 * A homepage feature heading: the feature's icon beside its title. As it
 * scrolls into view the icon assembles from its pieces, each flying in from
 * its own side and snapping together, while the title slides in towards it.
 * Plays once; renders at rest under prefers-reduced-motion. `onAssemble` is
 * called when it starts, for content meant to arrive alongside it.
 */
export default function SectionHeading({
  icon,
  title,
  justifyContent,
  onAssemble,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: TRIGGER_MARGIN });
  const reduce = useReducedMotion();
  const assembled = inView || reduce;
  const { viewBox, pieces } = ICONS[icon];

  useEffect(() => {
    if (assembled) onAssemble?.();
  }, [assembled, onAssemble]);

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      justifyContent={justifyContent}
      spacing={-5}
      mb={2}
    >
      <SvgIcon
        viewBox={viewBox}
        sx={{
          width: { xs: 75, sm: 100 },
          height: { xs: 75, sm: 100 },
          color: "primary.main",
          // Pieces start outside the icon's box
          overflow: "visible",
        }}
      >
        {pieces.map((piece, index) => (
          <motion.path
            key={piece.d}
            d={piece.d}
            fill="currentColor"
            initial={reduce ? false : { ...piece.from, opacity: 0 }}
            animate={assembled ? REST : undefined}
            transition={{
              type: "spring",
              // Softer than the app's springs: a heading to watch, not a
              // control responding to a tap
              stiffness: 110,
              damping: 16,
              delay: index * PIECE_STAGGER,
              opacity: { duration: 0.2, delay: index * PIECE_STAGGER },
            }}
          />
        ))}
      </SvgIcon>
      <Typography
        component={motion.h3}
        variant="h3"
        color="text.primary"
        initial={reduce ? false : { opacity: 0, x: 24 }}
        animate={assembled ? { opacity: 1, x: 0 } : undefined}
        transition={{ duration: 0.6, delay: TITLE_DELAY, ease: "easeOut" }}
      >
        {title}
      </Typography>
    </Stack>
  );
}
